/**
 * NL Segment Builder API — W12.
 *
 * Two endpoints:
 *   POST /v1/segments/from-nl      — prompt → preview (filter+audience+samples)
 *   POST /v1/segments/save-from-nl — confirm + save (audited with original prompt)
 *
 * The save step is ALWAYS manual: the UI shows the compiled filter + audience
 * count, the human picks a name and presses save.
 */
import { apiClient } from "./client";

export type NLConfidence = "high" | "medium" | "low";

export interface NLWarning {
  code: string;
  message: string;
  path?: string;
}

export interface NLProfileSummary {
  id: string;
  display_name: string | null;
  primary_email: string | null;
  primary_phone: string | null;
  tags: string[];
  lifecycle_stage: string | null;
}

export interface NLSegmentResult {
  filter: Record<string, unknown> | null;
  audience_size?: number;
  samples: NLProfileSummary[];
  explanation: string;
  confidence: NLConfidence;
  warnings: NLWarning[];
  prompt_used: string;
  provider: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  retried: boolean;
}

export interface SavedSegment {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  filter: Record<string, unknown>;
  kind: string;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export const nlSegmentApi = {
  async generateFromNL(prompt: string, language?: "ru" | "en"): Promise<NLSegmentResult> {
    const body: { prompt: string; language?: "ru" | "en" } = { prompt };
    if (language) body.language = language;
    const res = await apiClient.post<NLSegmentResult>("/v1/segments/from-nl", body);
    return res.data;
  },

  async saveFromNL(input: {
    prompt_used: string;
    filter: Record<string, unknown>;
    name: string;
    description?: string | null;
  }): Promise<SavedSegment> {
    const res = await apiClient.post<{ segment: SavedSegment }>("/v1/segments/save-from-nl", input);
    return res.data.segment;
  },
};
